import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import NodeForm from '@/components/nodes/NodeForm';
import ParamsProvider from '@/components/providers/ParamProvider';
import { nodeActions, useNode, useCustomModel, useCaseNodeParams, useNodeParams } from '@/stores/nodeStore';
import { derivedCacheActions } from '@/stores/derivedCacheStore';
import { paramActions } from '@/stores/paramStore';
import { updateNodeParams, fetchDerivedValues } from '@/utils/nodeUtils';
import { parseCategories } from '@/utils/paramsUtils';

// ---------------------------------------------------------------------------
// Inner component — rendered after node data is loaded into nodeStore.
// Mirrors NodeFormContent but uses our own ParamsProvider so the correct
// caseId/nodeId are used for PATCH calls (not the URL params from useParams).
// ---------------------------------------------------------------------------

function NodeEditorInner({ nodeId, patch, fetchDerived, actions, targetParam }) {
  const node = useNode();
  const customModel = useCustomModel();
  const caseParams = useCaseNodeParams();
  const nodeParams = useNodeParams();

  if (!node || !caseParams) return null;

  const categories = parseCategories(nodeParams);
  if (node.description || customModel?.is_creator) {
    categories.Description = { categoryName: 'Info', params: [] };
  }

  // If a targetParam was requested, find which category tab it lives in.
  let initialCategory;
  if (targetParam) {
    for (const { categoryName, params } of Object.values(categories)) {
      if (params.some(p => p.name === targetParam)) {
        initialCategory = categoryName;
        break;
      }
    }
  }

  return (
    <ParamsProvider patch={patch} fetchDerivedValues={fetchDerived}>
      <NodeForm
        key={nodeId}
        actions={actions}
        title={node.label}
        description={node.description}
        caseParams={caseParams}
        categories={categories}
        initialCategory={initialCategory}
        targetParam={targetParam}
      />
    </ParamsProvider>
  );
}

NodeEditorInner.propTypes = {
  nodeId: PropTypes.string,
  patch: PropTypes.func.isRequired,
  fetchDerived: PropTypes.func.isRequired,
  actions: PropTypes.node.isRequired,
  targetParam: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * Renders the full node param form for a given node inside the analyst
 * workspace. No nested router is used — instead, custom `patch` and
 * `fetchDerivedValues` functions close over the real caseId/nodeId and are
 * injected via ParamsProvider. The field components call these functions
 * with the URL params from useParams() as a second argument, which our
 * closures safely ignore in favour of the known IDs.
 *
 * Props:
 *   pathwayId  — external UUID of the pathway (needed for node schema fetch)
 *   caseId     — external UUID of the case (needed for params GET/PATCH)
 *   nodeId     — the node's string ID within the pathway
 *   onClose    — called when the user clicks Done
 */
export default function NodeParamEditor({ pathwayId, caseId, nodeId, onClose, targetParam }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    nodeActions.init(pathwayId, caseId, nodeId)
      .then(() => setLoading(false))
      .catch(e => {
        setError(e?.message ?? 'Failed to load parameters');
        setLoading(false);
      });

    return () => {
      derivedCacheActions.clear();
      paramActions.clear();
    };
  }, [pathwayId, caseId, nodeId]);

  // Close over caseId/nodeId so URL params from useParams() are irrelevant.
  const patch = useCallback(
    (data, _urlParams) => updateNodeParams(data, { caseId, nodeId }),
    [caseId, nodeId],
  );

  const fetchDerived = useCallback(
    (name, _urlParams) => fetchDerivedValues(name, { caseId, nodeId }),
    [caseId, nodeId],
  );

  const actions = (
    <button
      type="button"
      className="btn btn-sm btn-ghost text-xs mr-1"
      onClick={onClose}
    >
      Done
    </button>
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="loading loading-spinner loading-sm text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-sm text-red-500">{error}</div>
    );
  }

  return (
    <NodeEditorInner
      nodeId={nodeId}
      patch={patch}
      fetchDerived={fetchDerived}
      actions={actions}
      targetParam={targetParam}
    />
  );
}

NodeParamEditor.propTypes = {
  pathwayId: PropTypes.string.isRequired,
  caseId: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  targetParam: PropTypes.string,
};
